(ns bec-core.cljs.components.markdown
  (:require [reagent.core :as r]
            [re-frame.core :as rf]
            [bec-cljs-common.utils.interop :as interop]
            ["react-markdown" :default react-markdown]
            ["remark-gfm" :default remark-gfm]))

(defonce markdown-text (r/atom 
                         "This a test of all markdown possibilities:\n\n------------------------------------------\n\n## Headings\n\n# h1 Heading 1\n## h2 Heading 2\n### h3 Heading 3\n#### h4 Heading 4\n##### h5 Heading 5\n###### h6 Heading 6\n\n------------------------------------------\n\n## Horizontal Rules\n\n___\n\n---\n\n***\n\n------------------------------------------\n\n## Emphasis\n\n**This is bold text**\n\n__This is bold text__\n\n*This is italic text*\n\n_This is italic text_\n\n~~Strikethrough~~\n\n------------------------------------------\n\n## Links\n\n[link text][1]\n\n[link with title][2]\n\nThis is [an example](http://example.com/ \"Title\") inline link.\n\n[This link](http://example.net/) has no title attribute.\n\n------------------------------------------\n\n## Blockquotes\n\n> Blockquotes can also be nested...\n>> ...by using additional greater-than signs right next to each other...\n> > > ...or with spaces between arrows.\n\n------------------------------------------\n\n## Indentation\n\n  indentation 1-1\n\nindentation 1-2\n    indentation 2-1\n    \n\n------------------------------------------\n\n## Lists\n\n### Unordered\n\n+ Create a list by starting a line with `+`, `-`, or `*`\n+ Sub-lists are made by indenting 2 spaces:\n  - Marker character change forces new list start:\n    * Ac tristique libero volutpat at\n    + Facilisis in pretium nisl aliquet\n    - Nulla volutpat aliquam velit\n+ Very easy!\n\n### Ordered\n\n#### Numers in sequence\n\n1. Lorem ipsum dolor sit amet\n2. Consectetur adipiscing elit\n3. Integer molestie lorem at massa\n\n#### Numers not in sequence\n\n1. You can use sequential numbers...\n1. ...or keep all the numbers as `1.`\n\n------------------------------------------\n\n## Images\n\n![Minion][3]\n![Stormtroopocat][4]\n\nLike links, Images also have a footnote style syntax\n\n![Alt text][5]\n\nWith a reference later in the document defining the URL location:\n\n\n\n------------------------------------------\n\n## Tables\n\n| Option | Description |\n| ------ | ----------- |\n| data   | path to data files to supply the data that will be passed into templates. |\n| engine | engine to be used for processing templates. Handlebars is the default. |\n| ext    | extension to be used for dest files. |\n\nRight aligned columns\n\n| Option | Description |\n| ------:| -----------:|\n| data   | path to data files to supply the data that will be passed into templates. |\n| engine | engine to be used for processing templates. Handlebars is the default. |\n| ext    | extension to be used for dest files. |\n\n------------------------------------------\n\n## Code\n\nInline `code`\n\nIndented code\n\n    // Some comments\n    line 1 of code\n    line 2 of code\n    line 3 of code\n\n\nBlock code \"fences\"\n\n```\nSample text here...\n```\n\nSyntax highlighting\n\n``` js\nvar foo = function (bar) {\n  return bar++;\n};\n\nconsole.log(foo(5));\n```\n\n------------------------------------------\n\n\n[1]: http://dev.nodeca.com\n[2]: http://nodeca.github.io/pica/demo/ \"title text!\"\n[3]: https://octodex.github.com/images/dinotocat.png\n[4]: https://octodex.github.com/images/saritocat.png \"The Stormtroopocat\"\n[5]: https://octodex.github.com/images/daftpunktocat-thomas.gif \"The Dojocat\""))

(defonce markdown-variables (r/atom {:preview? true
                                     :edit? true
                                     :height "20em"}))

(defn markdown-viewer [& [props]]
  (r/create-class
    {:component-did-mount
     (fn [this]
       (let [props (second (.-argv (.-props this)))]
         (when (and (not (:preview-transitioning? (:markdown-variables props)))
                 (:preview? (:markdown-variables props))
                 (:editor? props))
           (when-let [element (interop/get-element-by-id "react-markdown")]
             (swap! markdown-variables assoc
               :height (if (> (-> element .-offsetHeight) 150)
                         (-> element .-offsetHeight)
                         150))))))
     :component-will-unmount
     (fn [this]
       (let [props (second (.-argv (.-props this)))]
         (when (and (not (:preview-transitioning? (:markdown-variables props)))
                 (:preview? (:markdown-variables props))
                 (:editor? props))
           (reset! markdown-variables {:preview? true
                                       :edit? true
                                       :height (:height (:markdown-variables props))}))))
     :component-did-update
     (fn [this]
       (let [props (second (.-argv (.-props this)))]
         (when (and (not (:preview-transitioning? (:markdown-variables props)))
                 (:preview? (:markdown-variables props))
                 (:editor? props))
           (when-let [element (interop/get-element-by-id "react-markdown")]
             (swap! markdown-variables assoc
               :height (if (> (-> element .-offsetHeight) 150)
                         (-> element .-offsetHeight)
                         150))))))
     :display-name "markdown-viewer"
     :reagent-render
     (fn [props]
       [:div#react-markdown
        (into
          [:> react-markdown
           {:className "content"
            :remarkPlugins [remark-gfm]}]
          (r/children (r/current-component)))])}))

;; TODO: make this customizable through props. Lots of hardcoded values to make it look good on bec-core
(defn markdown-editor [& [props]]
  (let [window-size (rf/subscribe [:window-size])
        height (str "calc(" (:height @markdown-variables) "px + 8rem)")
        transition "padding calc(var(--transition-duration, 250ms) * 1.5), width calc(var(--transition-duration, 250ms) * 1.5), height calc(var(--transition-duration, 250ms) * 1.5)"]
    [:div
     [:div
      {:style {:display "flex"
               :justify-content "center"
               :margin "1rem 0 0 0"}}
      [:button.button.is-fullwidth
       {:style {:margin-right "0.5rem"}
        :on-click #(swap! markdown-variables assoc
                     :edit? (not (:edit? @markdown-variables))
                     :preview? true
                     :preview-transitioning? true)}
       (if (:edit? @markdown-variables)
         "Hide editing"
         "Edit markdown")]
      [:button.button.is-fullwidth
       {:style {:margin-left "0.5rem"}
        :on-click #(swap! markdown-variables assoc
                     :preview? (not (:preview? @markdown-variables))
                     :preview-transitioning? true
                     :edit? true)}
       (if (:preview? @markdown-variables)
         "Hide preview"
         "Show preview")]]
     [:div.tile.is-ancestor
      [:div.tile.is-parent.is-vertical
       {:class (cond
                 (not (:edit? @markdown-variables)) "is-0"
                 (:preview? @markdown-variables) "is-6"
                 :else "is-12")
        :style {:width (when (and (not (:edit? @markdown-variables)) (> (:width @window-size) 768)) 0)
                :height (if (:edit? @markdown-variables)
                          (if (> (:width @window-size) 768)
                            height
                            (if (:preview? @markdown-variables)
                              "20em"
                              "42em"))
                          (if (> (:width @window-size) 768)
                            height
                            0))
                :padding (when-not (:edit? @markdown-variables) 0)
                :overflow "hidden"
                :transition transition}
        :on-transition-end #(swap! markdown-variables assoc :preview-transitioning? false)}
       [:p.title
        {:style {:max-height "4rem"}}
        "Edit markdown"]
       [:div.tile.is-child {:style {:height "calc(100% - 8rem)"}}
        [:textarea.textarea.has-fixed-size
         {:style {:max-height "none"
                  :height "100%"}
          :auto-focus true
          :value @markdown-text
          :on-change (fn [event] 
                       (swap! markdown-variables assoc :has-been-edited? true)
                       (reset! markdown-text (-> event .-target .-value)))}]]]
      [:div.tile.is-parent.is-vertical
       {:class (cond
                 (not (:preview? @markdown-variables)) "is-0"
                 (:edit? @markdown-variables) "is-6"
                 :else "is-12")
        :style {:width (when (and (not (:preview? @markdown-variables)) (> (:width @window-size) 768)) 0)
                :height (if (:preview? @markdown-variables)
                          height
                          0)
                :padding (when-not (:preview? @markdown-variables) 0)
                :overflow "hidden"
                :transition transition}
        :on-transition-end #(swap! markdown-variables assoc :preview-transitioning? false)}
       [:p.title
        {:style {:max-height "4rem"}}
        "Preview markdown"]
       [:div.tile.is-child
        {:style {:overflow (if (:preview-transitioning? @markdown-variables) "hidden" "auto")}}
        [markdown-viewer
         {:window-size @window-size
          :markdown-variables @markdown-variables
          :editor? true}
         @markdown-text]]]]
     [:button.button.is-fullwidth.is-outlined
      {:class (if (:has-been-edited? @markdown-variables)
                "is-success"
                "is-info")
       :disabled (not (:has-been-edited? @markdown-variables))
       :on-click (fn []
                   (swap! markdown-variables assoc :has-been-edited? false))}
      "Save"]]))